import { useEffect, useRef, useState } from 'react';

/**
 * Generic undo/redo stack.
 *
 * - `reset(baseline)` — clears history and records the current state as the
 *   sole baseline entry. Call this when the user activates a transform mode so
 *   they can only undo back to that activation point.
 * - `push(state)` — appends a new entry and discards any redo future.
 * - `undo()` — moves back one step and returns the state to restore, or null.
 * - `redo()` — moves forward one step and returns the state to restore, or null.
 */
export function useUndoRedo<T>(maxHistory = 50) {
  // stack[index] is always the "present" committed state.
  const [, forceRender] = useState(0);
  const stackRef = useRef<T[]>([]);
  const indexRef = useRef(-1);

  const rerender = () => forceRender((n) => n + 1);

  const reset = (baseline: T) => {
    stackRef.current = [baseline];
    indexRef.current = 0;
    rerender();
  };

  const push = (state: T) => {
    const stack = stackRef.current.slice(0, indexRef.current + 1);
    stack.push(state);
    if (stack.length > maxHistory) stack.splice(0, stack.length - maxHistory);
    stackRef.current = stack;
    indexRef.current = stack.length - 1;
    rerender();
  };

  const undo = (): T | null => {
    if (indexRef.current <= 0) return null;
    indexRef.current -= 1;
    rerender();
    return stackRef.current[indexRef.current];
  };

  const redo = (): T | null => {
    if (indexRef.current >= stackRef.current.length - 1) return null;
    indexRef.current += 1;
    rerender();
    return stackRef.current[indexRef.current];
  };

  const canUndo = indexRef.current > 0;
  const canRedo = indexRef.current < stackRef.current.length - 1;

  return { push, undo, redo, canUndo, canRedo, reset };
}

/**
 * Registers Ctrl+Z (undo) and Ctrl+Y / Ctrl+Shift+Z (redo) keyboard shortcuts.
 * Uses refs internally so it registers once on mount and always calls the latest
 * callbacks — no need to re-register when undo/redo/apply change.
 * Skips when a form element (INPUT, TEXTAREA, SELECT) is focused.
 */
export function useUndoRedoKeyboard<T>(
  undo: () => T | null,
  redo: () => T | null,
  apply: (state: T) => void
): void {
  const undoRef = useRef(undo);
  const redoRef = useRef(redo);
  const applyRef = useRef(apply);
  undoRef.current = undo;
  redoRef.current = redo;
  applyRef.current = apply;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      const tag = (document.activeElement as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        const snap = undoRef.current();
        if (snap) applyRef.current(snap);
      } else if (e.key === 'y' || (e.key === 'z' && e.shiftKey)) {
        e.preventDefault();
        const snap = redoRef.current();
        if (snap) applyRef.current(snap);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);
}
